import { QualificationQuestion, QualificationSection } from '../lib/api';

// -- Local state types (with temp UUIDs before save) --

export interface LocalQuestion {
  _key: string;
  id?: number;
  question_text: string;
  question_type: QualificationQuestion['question_type'];
  help_text: string;
  is_required: boolean;
  is_scored: boolean;
  max_score: number;
  scoring_criteria: Record<string, unknown>;
  choices: string[];
}

export interface LocalSection {
  _key: string;
  id?: number;
  title: string;
  description: string;
  weight: number;
  is_required: boolean;
  questions: LocalQuestion[];
}

export const TEMPLATE_TYPES: Array<{ value: string; label: string }> = [
  { value: 'compliance', label: 'Compliance' },
  { value: 'quality', label: 'Quality' },
  { value: 'capability', label: 'Capability' },
  { value: 'financial', label: 'Financial' },
  { value: 'sustainability', label: 'Sustainability' },
  { value: 'security', label: 'Security' },
  { value: 'custom', label: 'Custom' },
];

export const QUESTION_TYPES: Array<{ value: QualificationQuestion['question_type']; label: string }> = [
  { value: 'text', label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'single_choice', label: 'Single Choice' },
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'file_upload', label: 'File Upload' },
  { value: 'rating', label: 'Rating' },
];

export const SECTION_COLORS = [
  { bg: 'bg-blue-50', border: 'border-blue-400', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700', ring: 'ring-blue-200' },
  { bg: 'bg-violet-50', border: 'border-violet-400', text: 'text-violet-700', badge: 'bg-violet-100 text-violet-700', ring: 'ring-violet-200' },
  { bg: 'bg-emerald-50', border: 'border-emerald-400', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700', ring: 'ring-emerald-200' },
  { bg: 'bg-amber-50', border: 'border-amber-400', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700', ring: 'ring-amber-200' },
  { bg: 'bg-rose-50', border: 'border-rose-400', text: 'text-rose-700', badge: 'bg-rose-100 text-rose-700', ring: 'ring-rose-200' },
  { bg: 'bg-cyan-50', border: 'border-cyan-400', text: 'text-cyan-700', badge: 'bg-cyan-100 text-cyan-700', ring: 'ring-cyan-200' },
];

let keyCounter = 0;
export function genKey(): string {
  keyCounter += 1;
  return `_tmp_${Date.now()}_${keyCounter}`;
}

export function makeQuestion(): LocalQuestion {
  return {
    _key: genKey(),
    question_text: '',
    question_type: 'text',
    help_text: '',
    is_required: true,
    is_scored: true,
    max_score: 10,
    scoring_criteria: {},
    choices: [],
  };
}

export function makeSection(): LocalSection {
  return {
    _key: genKey(),
    title: '',
    description: '',
    weight: 0,
    is_required: true,
    questions: [makeQuestion()],
  };
}

export function sectionFromApi(s: QualificationSection): LocalSection {
  return {
    _key: genKey(),
    id: s.id,
    title: s.title,
    description: s.description || '',
    weight: s.weight,
    is_required: s.is_required,
    questions: (s.questions || [])
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((q) => ({
        _key: genKey(),
        id: q.id,
        question_text: q.question_text,
        question_type: q.question_type,
        help_text: q.help_text || '',
        is_required: q.is_required,
        is_scored: q.is_scored,
        max_score: q.max_score,
        scoring_criteria: q.scoring_criteria || {},
        choices: q.choices || [],
      })),
  };
}

export function getQuestionTypeLabel(type: string): string {
  return QUESTION_TYPES.find((t) => t.value === type)?.label ?? type;
}
